import React from 'react';

import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { Feature } from '@techstyle/react-features';
import { FormattedMessage } from '@techstyle/react-intl';

import { desktop } from '../../styles';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { getActiveGiftCertCredits, isUSDomain } from '../../utils/selectors';
import { Component as Price } from '../ExtendedPrice';

import { useIsFreeShippingAvailableAndThresholdIsReached } from './NewCartDrawerProgressBar';
import { PaymentMessagingLine } from './PaymentMessagingLine';
import { ProgressBarTooltip } from './ProgressBarTooltip';
import { usePricingSummary } from './usePricingSummary';

// same as TotalPriceSummary
const afterpayClassName = 'afterpay-price-total';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  ${desktop`
    padding: ${({ theme }) => theme.spacing.small}px 0;
  `}

  .afterpay-paragraph {
    text-align: center;
    margin-top: 10px;

    .afterpay-logo,
    .afterpay-link {
      vertical-align: sub !important;
    }
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${({ theme }) => theme.paragraph.variants.paragraph4Uppercase.textStyles};
`;

const TotalRow = styled(Row)`
  ${({ theme }) => theme.subHeader.variants.subHeading4Uppercase.textStyles};
`;

const TotalRowDivider = styled.div`
  margin-bottom: ${props => props.theme.spacing.tiny}px;
  margin-top: ${props => props.theme.spacing.tiny}px;
  border-bottom: solid 1px ${({ theme }) => theme.colors.stone};
`;

const LabelWrapper = styled.div`
  display: flex;

  > div {
    margin-top: 2px;
  }
`;

const TariffLabel = () => {
  const { subtotal, subtotalDiscount, tariffSurchargeAmount } =
    usePricingSummary();

  const isUS = useSelector(isUSDomain);

  if (!isUS) {
    return null;
  }

  let tariffRate: string | number =
    (tariffSurchargeAmount ?? 0) / (subtotal - subtotalDiscount);

  switch (Math.floor(tariffRate * 100)) {
    case 2:
      tariffRate = '2';
      break;
    case 4:
      tariffRate = '4';
      break;
    case 5:
      tariffRate = '5.25';
      break;
    case 6:
      tariffRate = '6.25';
      break;
    case 8:
      tariffRate = '8.75';
      break;
    case 10:
      tariffRate = '10';
      break;
    case 11:
    case 12:
      if (tariffRate * 100 < 11.5) {
        tariffRate = '';
        break;
      }
      tariffRate = '12';
      break;
    default:
      tariffRate = '';
  }

  if (tariffRate) {
    return (
      <FormattedMessage
        id="global_checkout.tariff_test_887"
        defaultMessage="Please be advised that tariffs on fashion goods continue to apply and make products more expensive, including those imported from China. While many retailers raised their prices to offset the increased cost, we’ve added a {tariff}% surcharge to your purchase and are covering the remainder of the cost ourselves to minimize impact to our customers. We appreciate your understanding and patience as we work towards offering you the best value possible."
        values={{
          tariff: tariffRate,
        }}
      />
    );
  }

  return (
    <Feature handle="tariff_ab_test">
      {
        // @ts-ignore
        feature => {
          const shouldShowBiggerTariff =
            feature.data && feature.data.enabled && feature.data.value;
          return shouldShowBiggerTariff ? (
            <FormattedMessage
              id="global_checkout.tariff_explainer_525"
              defaultMessage="On September 1st, 2019, tariffs were increased on all fashion goods coming from China, making them 5.25% more expensive. We appreciate your understanding and patience as we work towards offering you the best value possible."
            />
          ) : (
            <FormattedMessage
              id="global_checkout.tariff_explainer_425"
              defaultMessage="On September 1st, 2019, tariffs were increased on all fashion goods coming from China, making them 4.25% more expensive. We appreciate your understanding and patience as we work towards offering you the best value possible."
            />
          );
        }
      }
    </Feature>
  );
};

const EmpCreditsVipValue = () => {
  const { creditsTotalValue, creditsApplied } = usePricingSummary();
  if (creditsApplied < 1) {
    return null;
  }
  return (
    <Row data-testid="CartDrawerSummaryCreditsApplied">
      <FormattedMessage
        id="global_checkout.member_credits_line_item"
        defaultMessage="{credits} Credit(s) Applied"
        values={{
          credits: creditsApplied,
        }}
      />
      <span>
        -
        <Price amount={creditsTotalValue} />
      </span>
    </Row>
  );
};

const SavingsOrDiscount = () => {
  const {
    cartLineDiscounts,
    discounts,
    subtotalDiscount,
    newMemberOfferDiscounts,
    subTotalDiscountWithoutMemberOffers: promoSavings,
  } = usePricingSummary();
  const showNewMemberOfferDiscounts =
    !isNaN(newMemberOfferDiscounts) && newMemberOfferDiscounts > 0;
  const showPromoSavings = !isNaN(promoSavings) && promoSavings > 0;
  if (
    cartLineDiscounts.length > 0 &&
    (showNewMemberOfferDiscounts || showPromoSavings)
  ) {
    return (
      <>
        {showNewMemberOfferDiscounts && (
          <Row data-testid="CartDrawerSummaryNewMemberDiscount">
            <FormattedMessage
              id="global_checkout.new_member_offer_label"
              defaultMessage="New member offer"
            />
            <span>
              -
              <Price amount={newMemberOfferDiscounts} />
            </span>
          </Row>
        )}
        {showPromoSavings && (
          <Row data-testid="CartDrawerSummarySavings">
            <FormattedMessage
              id="global_checkout.promo_savings_label"
              defaultMessage="Promo savings"
            />
            <span>
              -
              <Price amount={promoSavings} />
            </span>
          </Row>
        )}
        <EmpCreditsVipValue />
      </>
    );
  }
  return (
    <>
      {discounts.length > 0 ? (
        <>
          {discounts.map(({ promoLabel, label, amount, id }) => (
            <Row data-testid="CartDrawerSummaryDiscount" key={id}>
              <FormattedMessage
                id="global_checkout.promo_label"
                defaultMessage="Promo: {label}"
                values={{
                  label: promoLabel || label,
                }}
              />
              <span>
                -
                <Price amount={amount} />
              </span>
            </Row>
          ))}
        </>
      ) : (
        <Row data-testid="CartDrawerSummarySavings">
          <FormattedMessage
            id="global_checkout.savings"
            defaultMessage="Savings"
          />
          <span>
            -
            <Price amount={subtotalDiscount} />
          </span>
        </Row>
      )}
      <EmpCreditsVipValue />
    </>
  );
};

const GiftCardCreditsRows = giftCardCredits => {
  const credits = Array.isArray(giftCardCredits)
    ? giftCardCredits
    : giftCardCredits?.giftCardCredits;
  return credits?.map(cert => {
    return (
      <Row key={cert.creditId} data-testid="CartDrawerSummaryGiftCardCredits">
        <div>
          <FormattedMessage
            id="global_checkout.gift_cert"
            defaultMessage="Gift Card"
          />
          &nbsp;
          {cert.creditCode}
        </div>
        <div>
          -<Price showLoading={false} amount={cert.creditAmount} />
        </div>
      </Row>
    );
  });
};

export function NewCartDrawerSummary() {
  const {
    total,
    subtotal,
    shipping,
    subtotalDiscount,
    tax,
    tariffSurchargeAmount,
    retailDeliveryFee,
    storeCreditsOnly,
  } = usePricingSummary();

  const isFreeShipping = useIsFreeShippingAvailableAndThresholdIsReached();

  const { 'gift-cards': isGiftCardsEnabled } = useLDFlags();
  const giftCardCredits = useSelector(state => getActiveGiftCertCredits(state));

  return (
    <Container>
      <Row data-testid="CartDrawerSummarySubtotal">
        <FormattedMessage
          id="global_checkout.subtotal"
          defaultMessage="Subtotal"
        />
        <span>
          <Price amount={subtotal} />
        </span>
      </Row>
      {subtotalDiscount > 0 && <SavingsOrDiscount />}

      {!!storeCreditsOnly && (
        <Row data-testid="CartDrawerStoreCredits">
          <LabelWrapper>
            <FormattedMessage
              id="global_checkout.store_credit"
              defaultMessage="Store credit"
            />
          </LabelWrapper>
          <span>
            -
            <Price amount={storeCreditsOnly} />
          </span>
        </Row>
      )}
      <Row data-testid="CartDrawerSummaryShipping">
        <FormattedMessage
          id="global_checkout.shipping"
          defaultMessage="Shipping"
        />
        <span>
          {isFreeShipping ? (
            <FormattedMessage
              id="global_cta.free_shipping"
              defaultMessage="Free"
            />
          ) : shipping === 0 ? (
            <FormattedMessage
              id="global_checkout.calculated_at_checkout"
              defaultMessage="Calculated at checkout"
            />
          ) : (
            <Price amount={shipping} />
          )}
        </span>
      </Row>
      {!!tariffSurchargeAmount && (
        <Row data-qa-automation="orderSummaryRows">
          <LabelWrapper>
            <FormattedMessage
              id="global_checkout.tariff"
              defaultMessage="Tariff Tax"
            />
            <ProgressBarTooltip
              placement="bottom-start"
              anchorProps={{
                'data-autotag': 'tariff-tooltip',
                'data-qa-automation': 'TariffTooltip',
              }}
            >
              <TariffLabel />
            </ProgressBarTooltip>
          </LabelWrapper>
          <span>
            <Price amount={tariffSurchargeAmount} />
          </span>
        </Row>
      )}
      <Row data-testid="CartDrawerSummaryTax">
        <LabelWrapper>
          <FormattedMessage id="global_checkout.tax" defaultMessage="Tax" />
          <ProgressBarTooltip
            placement="bottom-start"
            anchorProps={{
              'data-autotag': 'tax-tooltip',
              'data-qa-automation': 'TaxTooltip',
            }}
          >
            <FormattedMessage
              defaultMessage="Taxes are based on your shipping location’s state and local taxes"
              id="global_checkout.taxes_shipping_tooltip"
            />
          </ProgressBarTooltip>
        </LabelWrapper>
        {tax === 0 ? (
          <FormattedMessage
            id="global_checkout.calculated_at_checkout"
            defaultMessage="Calculated at checkout"
          />
        ) : (
          <Price amount={tax} />
        )}
      </Row>
      {isGiftCardsEnabled && giftCardCredits && (
        <GiftCardCreditsRows giftCardCredits={giftCardCredits} />
      )}
      {!!retailDeliveryFee && (
        <Row data-testid="CartDrawerRetailDeliveryFee">
          <LabelWrapper>
            <FormattedMessage
              id="global_checkout.retail_delivery_fee"
              defaultMessage="Retail Delivery Fee"
              data-qa-automation="historyRetailDeliveryFeeText"
            />
            <ProgressBarTooltip
              placement="bottom-start"
              anchorProps={{
                'data-autotag': 'retail-delivery-fee-tooltip',
                'data-qa-automation': 'RetailDeliveryFeeTooltip',
              }}
            >
              <FormattedMessage
                id="global_checkout.retail_delivery_fee_tool_tip"
                defaultMessage="Effective July 1, 2022, Colorado imposes a non-refundable retail delivery fee of $0.27 on all deliveries by motor vehicle to a location in Colorado with at least one item of tangible personal property subject to state sales or use tax."
              />
            </ProgressBarTooltip>
          </LabelWrapper>
          <span>
            <Price amount={retailDeliveryFee} />
          </span>
        </Row>
      )}
      <TotalRowDivider />
      <TotalRow
        data-testid="CartDrawerSummaryTotal"
        className={afterpayClassName}
      >
        <FormattedMessage id="global_checkout.total" defaultMessage="Total" />
        <span>
          <Price amount={total} />
        </span>
      </TotalRow>
      <PaymentMessagingLine
        total={total}
        afterpayClassName={afterpayClassName}
      />
    </Container>
  );
}
